import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { adresseApi } from '../../api/client';
import { ouvreDocument } from '../../api/documents.js';
import { courriersApi, maSignatureApi, piecesApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { BoutonRediger } from '../../components/BoutonRediger.jsx';
import { BoutonMaSignature } from '../../components/MaSignature.jsx';
import { PhotoBtn } from '../../components/PhotoBtn.jsx';
import { SignaturePad } from '../../components/SignaturePad.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp, _inp } from '../../components/ui/Inputs.jsx';
import { lettreAEcran, lettreAImprimer } from '../../lib/courrier';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** La fonction de qui signe, d'après son rôle. */
const FONCTIONS = {
  patron: 'Direction',
  secretaire: 'Secrétariat',
  technicien: 'Technique',
  directeur: 'Direction des travaux',
  comptable: 'Comptabilité',
};

/**
 * Courrier libre.
 *
 * Tous les autres documents de l'application ont une forme imposée par leur
 * usage — une facture porte un QR, un contrat des clauses. Celui-ci n'a que
 * l'en-tête de l'entreprise et la présentation suisse : il sert à tout ce qui
 * n'entre dans aucune case.
 *
 * <p>Comme la décharge, il s'enregistre : texte, destinataire, signature et
 * photos se retrouvent dans « Mes courriers », et son PDF dans le classeur.
 */
export function CourrierPage({ onNav, soc, estMixte, univers, setUnivers }) {
  const { session } = useSession();
  const [courrierId, setCourrierId] = useState(null);
  const [numero, setNumero] = useState('');
  const [dest, setDest] = useState('');
  const [destAdr, setDestAdr] = useState('');
  const [objet, setObjet] = useState('');
  const [corps, setCorps] = useState('');
  // Le logo est proposé, jamais imposé : en tête de la lettre, en fond de
  // page comme sur les offres et les factures, ou pas du tout.
  const [placeLogo, setPlaceLogo] = useState('entete');
  // Qui signe : la personne connectée, patron, secrétaire ou technicien.
  // Le bureau signe de son nom et de sa fonction, jamais de ceux du patron :
  // « chacun a ses responsabilités ». Le serveur l'impose de son côté.
  const duBureau = !!session?.role && session.role !== 'patron';
  const [signataireNom, setSignataireNom] = useState(session?.nom || '');
  const [signataireFonction, setSignataireFonction] = useState(FONCTIONS[session?.role] || '');
  const [signature, setSignature] = useState('');
  // Chacun retrouve sa propre signature enregistrée sur chaque nouveau
  // courrier : le patron la sienne (ou celle de Réglages → Documents), le
  // bureau la sienne — jamais celle du patron.
  const poseMaSignature = useCallback(() => {
    Promise.resolve()
      .then(() => maSignatureApi.lit())
      .then((r) => {
        const image = r?.signature || (duBureau ? '' : soc?.sigPatron || '');
        if (image) setSignature((v) => v || image);
      })
      .catch(() => {});
  }, [duBureau, soc?.sigPatron]);
  useEffect(poseMaSignature, [poseMaSignature]);
  // Le tracé en cours : il devient la signature une fois validé.
  const [trace, setTrace] = useState('');
  // Envoi par e-mail : la lettre part en PDF, de la boîte de l'entreprise
  // (info@bektech.ch) quand elle est raccordée dans Réglages.
  const [email, setEmail] = useState('');
  const [envoi, setEnvoi] = useState({ etat: '', message: '' });
  // Photos jointes à la lettre, comme dans l'offre : elles suivent la
  // signature, deux par rangée, avec leur légende, à l'écran comme en PDF.
  // Une photo enregistrée porte son identifiant ; une nouvelle, son fichier.
  const [photos, setPhotos] = useState([]);
  const ajoutePhoto = (src, fichier) =>
    setPhotos((l) => (l.length >= PHOTOS_MAX ? l : [...l, { src, fichier, legende: '' }]));
  const legende = (i, v) => setPhotos((l) => l.map((p, j) => (j === i ? { ...p, legende: v } : p)));
  const retirePhoto = (i) => setPhotos((l) => l.filter((_, j) => j !== i));
  // Les courriers déjà enregistrés, à rouvrir.
  const [mesCourriers, setMesCourriers] = useState([]);

  const relis = useCallback(() => {
    Promise.resolve()
      .then(() => courriersApi.liste())
      .then((l) => setMesCourriers(Array.isArray(l) ? l : []))
      .catch(() => setMesCourriers([]));
  }, []);
  useEffect(() => {
    relis();
  }, [relis]);

  /**
   * Supprime un courrier enregistré — une lettre ratée, un essai. La question
   * se pose avant : la lettre et son PDF partent du classeur.
   *
   * @param {object} c courrier de la liste
   */
  const supprimeCourrier = (c) => {
    if (!window.confirm(trf('Supprimer le courrier n° {0} ?', c.numero || '—'))) return;
    Promise.resolve()
      .then(() => courriersApi.supprime(c.id))
      .then(() => {
        if (c.id === courrierId) nouveau();
        relis();
      })
      .catch((e) => window.alert(e?.message || tr('Le courrier n’a pas pu être supprimé.')));
  };

  // La lettre générée, montrée dans l'application. Elle s'ouvrait dans une
  // nouvelle fenêtre : sur le téléphone, on quittait SwissTech sans bouton
  // pour y revenir.
  const [lettre, setLettre] = useState(null);

  const incomplet = !dest.trim() || !corps.trim();

  const generer = async () => {
    if (incomplet) {
      window.alert('Remplis au moins le destinataire et le corps de la lettre.');
      return;
    }
    // Pas de lettre imprimée sans trace : elle s'enregistre d'abord, se range
    // au classeur, et le patron est prévenu quand le bureau l'a écrite.
    try {
      await enregistre();
    } catch (e) {
      setSauvegarde({
        etat: 'echec',
        message: e?.message || 'Le courrier n’a pas pu être enregistré.',
      });
      return;
    }
    const contenu = {
      dest,
      destAdr,
      objet,
      corps,
      logo: placeLogo,
      photos,
      signature,
      signataireNom: duBureau ? session?.nom || '' : signataireNom,
      signataireFonction: duBureau ? FONCTIONS[session?.role] || '' : signataireFonction,
    };
    setLettre({
      ecran: lettreAEcran(contenu, soc),
      impression: lettreAImprimer(contenu, soc),
    });
  };

  /**
   * Enregistre le courrier — le crée ou le met à jour — puis dépose ses
   * nouvelles photos. Rend son identifiant.
   */
  const enregistre = async () => {
    const contenu = {
      destinataireNom: dest.trim(),
      destinataireAdresse: destAdr || null,
      destinataireEmail: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? email.trim() : null,
      objet: objet || null,
      corps,
      logo: soc?.logo ? placeLogo : 'aucun',
      signataireNom: (duBureau ? session?.nom : signataireNom) || null,
      signataireFonction: (duBureau ? FONCTIONS[session?.role] : signataireFonction) || null,
      signature: signature || null,
    };
    const fait = courrierId
      ? await courriersApi.metAJour(courrierId, contenu)
      : await courriersApi.cree(contenu);
    const id = fait.id;
    setCourrierId(id);
    setNumero(fait.numero || '');
    const deposees = [];
    for (const p of photos) {
      if (p.pieceId || !p.fichier) {
        deposees.push(p);
        continue;
      }
      const piece = await piecesApi.depose(
        'COURRIER',
        id,
        p.fichier,
        p.legende.trim() || undefined,
      );
      deposees.push({ ...p, pieceId: piece.id, fichier: undefined });
    }
    setPhotos(deposees);
    relis();
    return id;
  };

  const [sauvegarde, setSauvegarde] = useState({ etat: '', message: '' });
  const enregistreSeul = async () => {
    if (incomplet) {
      setSauvegarde({
        etat: 'echec',
        message: 'Remplis au moins le destinataire et le corps de la lettre.',
      });
      return;
    }
    setSauvegarde({ etat: 'envoi', message: '' });
    try {
      await enregistre();
      setSauvegarde({
        etat: 'ok',
        message: 'Courrier enregistré : il est dans « Mes courriers » et dans le classeur.',
      });
    } catch (e) {
      setSauvegarde({
        etat: 'echec',
        message: e?.message || 'Le courrier n’a pas pu être enregistré.',
      });
    }
  };

  const pdfDuClasseur = async () => {
    if (incomplet) {
      setSauvegarde({
        etat: 'echec',
        message: 'Remplis au moins le destinataire et le corps de la lettre.',
      });
      return;
    }
    setSauvegarde({ etat: 'envoi', message: '' });
    try {
      const id = await enregistre();
      await ouvreDocument(`/courriers/${id}/document`);
      setSauvegarde({ etat: '', message: '' });
    } catch (e) {
      setSauvegarde({ etat: 'echec', message: e?.message || 'Le PDF n’a pas pu être produit.' });
    }
  };

  const envoieParEmail = async () => {
    if (incomplet) {
      setEnvoi({
        etat: 'echec',
        message: 'Remplis au moins le destinataire et le corps de la lettre.',
      });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEnvoi({ etat: 'echec', message: 'Indique l’adresse e-mail du destinataire.' });
      return;
    }
    setEnvoi({ etat: 'envoi', message: '' });
    try {
      const id = await enregistre();
      const r = await courriersApi.envoie(id, email.trim());
      setEnvoi({
        etat: 'ok',
        message: 'Envoyé à ' + (r?.destinataire || email.trim()) + ', PDF joint.',
      });
    } catch (e) {
      setEnvoi({ etat: 'echec', message: e?.message || 'La lettre n’a pas pu être envoyée.' });
    }
  };

  /** Rouvre un courrier enregistré, avec ses photos. */
  const ouvre = async (c) => {
    // Le courrier d'une autre personne s'ouvre en copie, à son propre nom et
    // sans la signature d'origine : on n'écrit pas par-dessus celle d'un autre.
    const enCopie =
      duBureau &&
      String(c.creePar || '').toLowerCase() !== String(session?.email || '').toLowerCase();
    setCourrierId(enCopie ? null : c.id);
    setNumero(enCopie ? '' : c.numero || '');
    setDest(c.destinataireNom || '');
    setDestAdr(c.destinataireAdresse || '');
    setEmail(c.destinataireEmail || '');
    setObjet(c.objet || '');
    setCorps(c.corps || '');
    setPlaceLogo(c.logo || 'entete');
    setSignataireNom(enCopie ? session?.nom || '' : c.signataireNom || '');
    setSignataireFonction(enCopie ? FONCTIONS[session?.role] || '' : c.signataireFonction || '');
    setSignature(enCopie ? '' : c.signature || '');
    setEnvoi({ etat: '', message: '' });
    setSauvegarde(
      enCopie
        ? {
            etat: 'ok',
            message:
              'Courrier écrit par ' +
              (c.signataireNom || c.creePar || 'une autre personne') +
              ' : il s’ouvre en copie, à ton nom.',
          }
        : { etat: '', message: '' },
    );
    if (enCopie) {
      setPhotos([]);
      poseMaSignature();
      return;
    }
    try {
      const pieces = await piecesApi.liste('COURRIER', c.id);
      setPhotos(
        (pieces || [])
          .filter((p) => p.fichier)
          .map((p) => ({ src: adresseApi(p.fichier), legende: p.libelle || '', pieceId: p.id })),
      );
    } catch {
      setPhotos([]);
    }
  };

  /** Un nouveau courrier, vierge. */
  const nouveau = () => {
    setCourrierId(null);
    setNumero('');
    setDest('');
    setDestAdr('');
    setEmail('');
    setObjet('');
    setCorps('');
    setPhotos([]);
    setSignature('');
    poseMaSignature();
    setSignataireNom(session?.nom || '');
    setSignataireFonction(FONCTIONS[session?.role] || '');
    setEnvoi({ etat: '', message: '' });
    setSauvegarde({ etat: '', message: '' });
  };

  /**
   * Imprime la feuille seule, ou l'enregistre en PDF. L'impression part de la
   * page de l'application, qui ne montre alors que la lettre : imprimer le
   * cadre imprimait, sur certains téléphones, la page entière.
   */
  const imprime = () => window.print();

  if (lettre) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          background: '#d9dde3',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            padding: '10px 12px',
            paddingTop: 'max(10px, var(--st-haut))',
            background: C.navy,
          }}
        >
          <button
            onClick={() => setLettre(null)}
            style={{
              border: 'none',
              borderRadius: 9,
              padding: '10px 14px',
              fontSize: 14,
              fontWeight: 800,
              cursor: 'pointer',
              background: 'rgba(255,255,255,.14)',
              color: '#fff',
            }}
          >
            ← Retour
          </button>
          <button
            onClick={imprime}
            style={{
              flex: 1,
              border: 'none',
              borderRadius: 9,
              padding: '10px 14px',
              fontSize: 14,
              fontWeight: 800,
              cursor: 'pointer',
              background: '#fff',
              color: C.navy,
            }}
          >
            🖨️ Imprimer ou enregistrer en PDF
          </button>
        </div>
        <ApercuLettre feuille={lettre.ecran} />
        {createPortal(
          <div id="impression-lettre">
            <style>{lettre.impression.css}</style>
            <div dangerouslySetInnerHTML={{ __html: lettre.impression.html }} />
          </div>,
          document.body,
        )}
      </div>
    );
  }

  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      back
      onBack={() => onNav('plus')}
      pageTitle="✉️ Courrier"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      {mesCourriers.length > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <Bar
            icon="📁"
            title="Mes courriers"
            color={C.navy}
            sub="Enregistrés : rouvrir, corriger, renvoyer"
          />
          <div style={{ padding: '6px 13px 10px' }}>
            {mesCourriers.slice(0, 30).map((c) => (
              <div
                key={c.id}
                data-testid="mon-courrier"
                onClick={() => ouvre(c)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '8px 0',
                  borderTop: '1px solid ' + C.border,
                  cursor: 'pointer',
                  fontSize: 10.5,
                  background: c.id === courrierId ? C.navyXL : 'transparent',
                }}
              >
                {/* Le nom et l'objet sont saisis : « Remise des clés » ne se traduit pas. */}
                <span style={{ minWidth: 0 }} translate="no">
                  <b>{c.destinataireNom}</b>
                  {c.objet ? <span style={{ color: C.muted }}> · {c.objet}</span> : null}
                </span>
                <span style={{ color: C.muted, flexShrink: 0 }}>
                  {trf('N° {0}', c.numero || '—')} ·{' '}
                  {String(c.dateDocument || '')
                    .split('-')
                    .reverse()
                    .join('.')}
                  <button
                    type="button"
                    aria-label={tr('Supprimer le courrier')}
                    onClick={(e) => {
                      e.stopPropagation();
                      supprimeCourrier(c);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: C.muted,
                      cursor: 'pointer',
                      marginLeft: 8,
                      fontSize: 12,
                    }}
                  >
                    ✕
                  </button>
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
      <Card style={{ marginBottom: 12 }}>
        <Bar
          icon="✉️"
          title={courrierId ? trf('Courrier n° {0}', numero || '—') : 'Nouveau courrier'}
          color={C.navy}
          sub="Une lettre à n'importe qui · tes coordonnées sont ajoutées"
        />
        <div style={{ padding: 13 }}>
          <Inp
            label="Destinataire — Nom"
            value={dest}
            onChange={(e) => setDest(e.target.value)}
            placeholder="Ex : Régie Dupont SA"
          />
          <Inp
            label="Destinataire — Adresse"
            rows={2}
            value={destAdr}
            onChange={(e) => setDestAdr(e.target.value)}
            placeholder="Rue, NPA et ville"
          />
          <Inp
            label="Objet"
            value={objet}
            onChange={(e) => setObjet(e.target.value)}
            placeholder="Ex : Demande de rendez-vous"
          />
          <Inp
            label="Corps de la lettre (🎤 dicte avec le clavier ou écris)"
            rows={6}
            value={corps}
            onChange={(e) => setCorps(e.target.value)}
            placeholder="Madame, Monsieur, …"
          />
          {/*
            Le Pack IA propose un paragraphe a partir de l'objet et des notes
            deja tapees dans le corps ; la proposition s'insere dans le champ,
            que l'utilisateur relit avant de generer la lettre.
          */}
          <BoutonRediger genre="courrier" titre={objet} mots={corps} onTexte={setCorps} full />
          <div style={{ marginTop: 10 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: C.muted,
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              {trf('Photos ({0}/{1})', photos.length, PHOTOS_MAX)}
            </div>
            {photos.map((p, i) => (
              <div
                key={i}
                data-testid="photo-lettre"
                style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}
              >
                <img
                  src={p.src}
                  alt=""
                  style={{
                    width: 56,
                    height: 56,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid ' + C.border,
                    flexShrink: 0,
                  }}
                />
                <input
                  aria-label={tr('Légende de la photo')}
                  value={p.legende}
                  onChange={(e) => legende(i, e.target.value)}
                  placeholder={tr('Légende (facultatif)')}
                  maxLength={300}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: '1px solid ' + C.border,
                    borderRadius: 8,
                    padding: '9px 10px',
                    fontSize: 16,
                    fontFamily: 'inherit',
                  }}
                />
                <button
                  type="button"
                  aria-label={tr('Retirer la photo')}
                  onClick={() => retirePhoto(i)}
                  style={{
                    border: 'none',
                    background: C.navyXL,
                    color: C.red,
                    borderRadius: 8,
                    width: 36,
                    height: 36,
                    fontSize: 14,
                    fontWeight: 800,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
            {photos.length < PHOTOS_MAX ? (
              <PhotoBtn
                label="📷 Ajouter une photo"
                color={C.navy}
                full
                cote={COTE_PHOTO_LETTRE}
                onPhoto={ajoutePhoto}
              />
            ) : null}
          </div>
          {soc?.logo ? (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 10,
                  fontWeight: 800,
                  color: C.muted,
                  textTransform: 'uppercase',
                  marginBottom: 6,
                }}
              >
                <img
                  src={soc.logo}
                  alt=""
                  style={{ maxHeight: 22, maxWidth: 64, objectFit: 'contain' }}
                />
                Logo de l&apos;entreprise
              </div>
              <div
                role="radiogroup"
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}
              >
                {[
                  ['entete', 'En haut de la lettre'],
                  ['fond', 'En fond de page'],
                  ['les-deux', 'En haut et en fond'],
                  ['aucun', 'Sans logo'],
                ].map(([v, l]) => {
                  const on = placeLogo === v;
                  return (
                    <div
                      key={v}
                      role="radio"
                      aria-checked={on}
                      onClick={() => setPlaceLogo(v)}
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        padding: '10px 6px',
                        borderRadius: 10,
                        cursor: 'pointer',
                        fontSize: 11,
                        fontWeight: 700,
                        border: '1px solid ' + (on ? C.navy : C.border),
                        background: on ? C.navy : '#fff',
                        color: on ? '#fff' : C.ink,
                      }}
                    >
                      {l}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          <div
            data-testid="signature-courrier"
            style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid ' + C.border }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: C.muted,
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              Signature
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {duBureau ? (
                <>
                  <Inp label="Signé par">
                    <div style={{ ..._inp, background: C.navyXL }}>{session?.nom || ''}</div>
                  </Inp>
                  <Inp label="Fonction">
                    <div style={{ ..._inp, background: C.navyXL }}>
                      {FONCTIONS[session?.role] || ''}
                    </div>
                  </Inp>
                </>
              ) : (
                <>
                  <Inp
                    label="Signé par"
                    value={signataireNom}
                    onChange={(e) => setSignataireNom(e.target.value)}
                  />
                  <Inp
                    label="Fonction"
                    value={signataireFonction}
                    onChange={(e) => setSignataireFonction(e.target.value)}
                  />
                </>
              )}
            </div>
            {duBureau && (
              <div style={{ fontSize: 11, color: C.muted, margin: '-4px 0 8px' }}>
                Le courrier part à ton nom et avec ta signature : chacun signe ce qu’il écrit.
              </div>
            )}
            {signature ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img
                  src={signature}
                  alt="Signature"
                  style={{
                    maxHeight: 60,
                    maxWidth: '60%',
                    background: '#fff',
                    border: '1px solid ' + C.border,
                    borderRadius: 8,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setSignature('')}
                  style={{
                    border: '1px solid ' + C.red,
                    background: C.white,
                    color: C.red,
                    borderRadius: 8,
                    padding: '7px 10px',
                    fontSize: 10,
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  Retirer
                </button>
              </div>
            ) : (
              <>
                <BoutonMaSignature soc={soc} onUtilise={setSignature} />
                <SignaturePad onSign={setTrace} />
                <button
                  type="button"
                  disabled={!trace}
                  onClick={() => {
                    setSignature(trace);
                    setTrace('');
                  }}
                  style={{
                    marginTop: 6,
                    width: '100%',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px',
                    fontSize: 10.5,
                    fontWeight: 800,
                    cursor: trace ? 'pointer' : 'default',
                    background: trace ? C.navy : C.border,
                    color: '#fff',
                  }}
                >
                  ✓ Valider la signature
                </button>
              </>
            )}
          </div>
          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Btn
              label={sauvegarde.etat === 'envoi' ? '…' : '💾 Enregistrer'}
              color={C.navy}
              full
              onClick={sauvegarde.etat === 'envoi' ? undefined : enregistreSeul}
            />
            <Btn label="📄 PDF (classeur)" color={C.teal} full onClick={pdfDuClasseur} />
          </div>
          {sauvegarde.message && (
            <div
              role="status"
              style={{
                fontSize: 11,
                fontWeight: 700,
                marginTop: 6,
                color: sauvegarde.etat === 'echec' ? C.red : C.green,
              }}
            >
              {sauvegarde.etat === 'echec' ? '✕ ' : '✓ '}
              {sauvegarde.message}
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <Btn
              label="📄 Générer la lettre (A4 · imprimer / PDF)"
              color={C.green}
              full
              onClick={generer}
            />
          </div>
          {courrierId ? (
            <button
              type="button"
              onClick={nouveau}
              style={{
                marginTop: 8,
                width: '100%',
                border: '1px dashed ' + C.navy,
                background: 'transparent',
                color: C.navy,
                borderRadius: 9,
                padding: '9px',
                fontSize: 10.5,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              ＋ Nouveau courrier
            </button>
          ) : null}
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid ' + C.border }}>
            <Inp
              label="E-mail du destinataire"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEnvoi({ etat: '', message: '' });
              }}
              placeholder="ex. regie@exemple.ch"
            />
            <Btn
              label={envoi.etat === 'envoi' ? 'Envoi en cours…' : '📨 Envoyer par e-mail (PDF)'}
              color={C.teal}
              full
              onClick={envoi.etat === 'envoi' ? undefined : envoieParEmail}
            />
            {envoi.message && (
              <div
                role="status"
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  marginTop: 6,
                  color: envoi.etat === 'echec' ? C.red : C.green,
                }}
              >
                {envoi.etat === 'echec' ? '✕ ' : '✓ '}
                {envoi.message}
              </div>
            )}
          </div>
        </div>
      </Card>
      <div
        style={{
          fontSize: 9,
          color: C.muted,
          textAlign: 'center',
          padding: '0 10px',
          lineHeight: 1.5,
        }}
      >
        La lettre s&apos;ouvre en feuille A4, telle qu&apos;elle sortira : adresse placée pour une
        enveloppe à fenêtre, bouton pour l&apos;imprimer ou l&apos;enregistrer en PDF. Tes
        coordonnées sont mises automatiquement en haut.
      </div>
    </Shell>
  );
}

/** Nombre de photos qu'une lettre peut porter : le PDF reste léger à envoyer. */
export const PHOTOS_MAX = 8;

/** Côté le plus long d'une photo de lettre : nette sur la feuille, légère en PDF. */
const COTE_PHOTO_LETTRE = 1200;

/** Marge autour de la feuille, à l'écran (px). */
const MARGE_APERCU = 10;

/**
 * La feuille A4 montrée dans l'application, réduite à la largeur de l'écran,
 * quel qu'il soit (téléphone, tablette, ordinateur) : elle garde sa mise en page
 * réelle et se lit entière, sans déborder.
 */
function ApercuLettre({ feuille }) {
  const zone = useRef(null);
  const page = useRef(null);
  const [taille, setTaille] = useState({ k: 1, l: 0, h: 0 });

  useLayoutEffect(() => {
    const mesure = () => {
      if (!zone.current || !page.current) return;
      const l = page.current.offsetWidth;
      const h = page.current.offsetHeight;
      const dispo = zone.current.clientWidth - 2 * MARGE_APERCU;
      const k = l > 0 && dispo > 0 ? Math.min(1, dispo / l) : 1;
      setTaille((t) => (t.k === k && t.l === l && t.h === h ? t : { k, l, h }));
    };
    mesure();
    window.addEventListener('resize', mesure);
    window.addEventListener('orientationchange', mesure);
    // Le logo arrive après la feuille : sa hauteur change encore.
    const obs = typeof ResizeObserver === 'function' ? new ResizeObserver(mesure) : null;
    if (obs) {
      obs.observe(zone.current);
      obs.observe(page.current);
    }
    return () => {
      window.removeEventListener('resize', mesure);
      window.removeEventListener('orientationchange', mesure);
      if (obs) obs.disconnect();
    };
  }, [feuille]);

  return (
    <div
      ref={zone}
      role="document"
      aria-label="Courrier"
      style={{
        flex: 1,
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: '#d9dde3',
        padding: MARGE_APERCU + 'px 0',
      }}
    >
      <div
        style={{
          width: taille.l ? taille.l * taille.k : 'auto',
          height: taille.h ? taille.h * taille.k : 'auto',
          margin: '0 auto',
          overflow: 'hidden',
        }}
      >
        <div
          ref={page}
          id="apercu-lettre"
          style={{ transform: 'scale(' + taille.k + ')', transformOrigin: '0 0' }}
        >
          <style>{feuille.css}</style>
          <div dangerouslySetInnerHTML={{ __html: feuille.html }} />
        </div>
      </div>
    </div>
  );
}
